import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { TableBody as Body, CircularProgress, Tooltip } from '@mui/material';
import dayjs from 'dayjs';

import { HeaderProperties } from '../../../../shared/uiComponents/Table/tableProperties';
import { errorNormalizer, formatClientName } from '../../../../shared/Helpers/functions';
import { useCheckPermission } from '../../../../shared/Helpers/hooks';
import { Badge, Text } from '../../../../shared/uiComponents';
import { BadgeTypes, SoapNoteStatusTypes, colors } from '../../../../shared/uiComponents/StatusBadge';
import { EditSvg, View } from '../../../../shared/Assets/svgIcons';
import { CustomTableRow, RowTableCell } from '../../../../shared/uiComponents/Table/tableHelpers';
import CustomDrawer from '../../../../shared/uiComponents/Drawer';
import { PERMISSIONS } from '../../../../App/constants';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { DSUSoapNoteProperties } from '../../../../redux/API/ClientAPIHelpers/soapNoteProperties';
import { SoapNotesStatuses } from '../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { getSoapNotePDF } from '../../../../redux/State/exportSlice';
import { setBcbaStatuses, setBtStatuses } from '../../../../redux/State/clientSlice/soapNoteSlice';
import { clearSessionInfo } from '../../../../redux/State/clientSlice/sessionSlice';
import { PEARL } from '../../../../shared/Helpers/colors';

export const Headers: HeaderProperties[] = [
	{ id: '1', name: 'Provider', orderBy: 'user.fullName' },
	{ id: '2', name: 'Client', orderBy: 'client.fullName' },
	{ id: '3', name: 'Session Date', orderBy: 'date', width: '200px' },
	{ id: '4', name: 'BCBA SOAP Note Status', width: '200px' },
	{ id: '5', name: 'BT SOAP Note Status', width: '200px' },
	{ id: '6', name: 'Action', width: '100px' },
];

export interface RowRendererProperties {
	data: DSUSoapNoteProperties[];
}

export const ViewSoapNote = ({ item }: { item: DSUSoapNoteProperties }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const EDIT = useCheckPermission(PERMISSIONS.SOAPNOTE.EDIT);

	const viewSoapNoteHandler = () => {
		const { status, client, id, sessionId } = item;

		if (SoapNotesStatuses.submitted !== status.status) {
			dispatch(clearSessionInfo());
			const query = new URLSearchParams();
			query.set('reportId', id);
			query.set('sessionId', sessionId);
			query.set('clientId', client.id);
			navigate(`/soap-notes/edit?${query.toString()}`);

			return;
		}
		setLoading(true);
		dispatch(
			getSoapNotePDF({
				reportId: id,
				includeTrials: true,
			}),
		)
			.then(unwrapResult)
			.then((response) => {
				const objectURL = URL.createObjectURL(response);
				window.open(objectURL);
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	if (loading) {
		return (
			<div style={{ width: '24px', height: '24px' }}>
				<CircularProgress size={16} />
			</div>
		);
	}

	const isEditable =
		EDIT.permissionGranted &&
		(item.status.status === SoapNotesStatuses.unlocked ||
			item.status.status === SoapNotesStatuses.draft ||
			item.status.status === SoapNotesStatuses.pendingBT);

	return (
		<Tooltip title={isEditable ? 'Edit' : 'View'}>
			<div onClick={viewSoapNoteHandler} style={{ height: '20px' }}>
				{isEditable ? <EditSvg color={PEARL.BASE[600]} /> : <View />}
			</div>
		</Tooltip>
	);
};

export const TableBody: FC<RowRendererProperties> = ({ data }) => (
	<Body>
		{data.map((row, index) => {
			const { user, client, date, status, supervisedSessionStatus } = row;
			const btStatusLabel = supervisedSessionStatus ? supervisedSessionStatus.name : '-';

			const btStatusType = supervisedSessionStatus ? supervisedSessionStatus.status : '-';

			return (
				<CustomTableRow key={index}>
					<RowTableCell>{user.fullName}</RowTableCell>
					<RowTableCell>{formatClientName(client.fullName, client.isElc)}</RowTableCell>
					<RowTableCell>{dayjs.utc(date).format('MM-DD-YYYY')}</RowTableCell>
					<RowTableCell>
						<Badge label={status.name} sizeType="lg" type={SoapNoteStatusTypes[status.status] as BadgeTypes} />
					</RowTableCell>
					<RowTableCell>
						<Badge label={btStatusLabel} sizeType="lg" type={SoapNoteStatusTypes[btStatusType]} />
					</RowTableCell>
					<RowTableCell>
						<ViewSoapNote item={row} />
					</RowTableCell>
				</CustomTableRow>
			);
		})}
	</Body>
);

const BCBAStatuses = [
	{ id: '0', name: 'Draft' },
	{ id: '1', name: 'Submitted' },
	{ id: '3', name: 'Expired BCBA' },
	{ id: '4', name: 'Unlocked' },
	{ id: '5', name: 'Pending BT' },
];

const BTStatuses = [
	{ id: '-1', name: 'Not Created' },
	{ id: '0', name: 'Draft' },
	{ id: '1', name: 'Submitted' },
	{ id: '2', name: 'Expired BT' },
	{ id: '4', name: 'Unlocked' },
];

export const DSUSoapNoteFilters = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params, setParams] = useSearchParams();
	const open = params.get('filter') === 'open';

	const bcbaNoteStatuses = useSelector((state) => state.soapNote.bcbaStatuses);
	const btNoteStatuses = useSelector((state) => state.soapNote.btStatuses);

	const closeHandler = () => {
		params.set('filter', 'close');
		setParams(params, { replace: true });
	};

	const bcbaStatusesHandler = (id: string) => {
		if (!bcbaNoteStatuses.includes(id)) {
			const data = [...bcbaNoteStatuses, id];
			dispatch(setBcbaStatuses(data));

			return;
		}
		const data = bcbaNoteStatuses.filter((x) => x !== id);
		dispatch(setBcbaStatuses(data));
	};

	const btStatusesHandler = (id: string) => {
		if (!btNoteStatuses.includes(id)) {
			const data = [...btNoteStatuses, id];
			dispatch(setBtStatuses(data));

			return;
		}
		const data = btNoteStatuses.filter((x) => x !== id);
		dispatch(setBtStatuses(data));
	};

	return (
		<CustomDrawer
			open={open}
			onClose={closeHandler}
			label="Filters"
			isSecondary
			Content={() => (
				<>
					<Text title="Select BCBA SOAP Note Status" type="h6" fontWeight="medium" font="header" />
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 16,
							alignItems: 'flex-start',
						}}
					>
						{BCBAStatuses.map((status) => {
							const type = bcbaNoteStatuses.includes(status.id) ? (SoapNoteStatusTypes[status.id] as BadgeTypes) : '';
							const color = colors[type];

							return (
								<div
									key={status.id}
									style={{
										cursor: 'pointer',
										width: 'max-content',
										outline: bcbaNoteStatuses.includes(status.id) ? `2px solid ${color}` : 'none',
										borderRadius: '999px',
									}}
								>
									<Badge
										label={status.name}
										sizeType="lg"
										type={SoapNoteStatusTypes[status.id] as BadgeTypes}
										onClick={() => bcbaStatusesHandler(status.id)}
									/>
								</div>
							);
						})}
					</div>
					<Text title="Select BT SOAP Note Status" type="h6" fontWeight="medium" font="header" />
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 16,
							alignItems: 'flex-start',
						}}
					>
						{BTStatuses.map((status) => {
							const type = btNoteStatuses.includes(status.id) ? (SoapNoteStatusTypes[status.id] as BadgeTypes) : '';
							const color = colors[type];

							return (
								<div
									key={status.id}
									style={{
										cursor: 'pointer',
										width: 'max-content',
										outline: btNoteStatuses.includes(status.id) ? `2px solid ${color}` : 'none',
										borderRadius: '999px',
									}}
								>
									<Badge
										label={status.name}
										sizeType="lg"
										type={SoapNoteStatusTypes[status.id] as BadgeTypes}
										onClick={() => btStatusesHandler(status.id)}
									/>
								</div>
							);
						})}
					</div>
				</>
			)}
		/>
	);
};
